import React, { useState, useEffect, useCallback } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { collection, query, where, orderBy, getDocs, limit } from "firebase/firestore";

const PreSendScorerSettings = ({ userId }) => {
  const [settings, setSettings] = useState({ enabled: false });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const settingsRef = ref(database, `/World/${userId}/settings/preSendScorerSettings`);
    const unsub = onValue(settingsRef, (snap) => {
      if (snap.exists()) setSettings(snap.val());
    });
    return () => unsub();
  }, [userId]);

  const loadStats = useCallback(async () => {
    if (!userId) return;
    setStatsLoading(true);
    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const logsRef = collection(firestore, `world_email_logs/${userId}/entries`);
      const q = query(logsRef, where("type", "==", "presend_score"), orderBy("date", "desc"), limit(200));
      const snap = await getDocs(q);

      let todayScored = 0;
      let todayBlocked = 0;
      let todayOverrides = 0;
      let totalBuyer = 0;
      let totalDeliv = 0;
      let todayCount = 0;
      const reasonMap = {};
      const recentScores = [];

      snap.docs.forEach((d) => {
        const data = d.data();
        const logDate = data.date ? new Date(data.date) : null;
        const isToday = logDate && logDate >= todayStart;

        if (isToday) {
          todayScored++;
          if (data.policyAction === "block") todayBlocked++;
          if (data.humanOverride) todayOverrides++;
          totalBuyer += data.buyerValue || 0;
          totalDeliv += data.deliverabilityRisk || 0;
          todayCount++;
        }

        if (recentScores.length < 10) {
          recentScores.push({
            score: data.compositeScore || 0,
            buyer: data.buyerValue || 0,
            deliv: data.deliverabilityRisk || 0,
            action: data.policyAction || "send",
            override: data.humanOverride || false,
            cached: data.fromCache || false,
            date: data.date,
          });
        }

        if (data.policyAction === "block" || (data.compositeScore && data.compositeScore <= 5)) {
          const tags = Array.isArray(data.reasonTags) ? data.reasonTags : [];
          tags.forEach((t) => {
            reasonMap[t] = (reasonMap[t] || 0) + 1;
          });
        }
      });

      const topReasons = Object.entries(reasonMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      setStats({
        todayScored,
        todayBlocked,
        todayOverrides,
        avgBuyer: todayCount ? (totalBuyer / todayCount).toFixed(1) : "–",
        avgDeliv: todayCount ? (totalDeliv / todayCount).toFixed(1) : "–",
        topReasons,
        recentScores,
        total: snap.size,
      });
    } catch (err) {
      console.error("Failed to load scorer stats:", err);
    }
    setStatsLoading(false);
  }, [userId]);

  useEffect(() => {
    if (settings.enabled && userId) loadStats();
  }, [settings.enabled, userId, loadStats]);

  if (!settings.enabled) return null;

  return (
    <div className="sg-stats">
      <header className="sg-card-head">
        <div>
          <p className="sg-kicker">Activity</p>
          <h4 className="sg-card-title">Scorer today</h4>
        </div>
        <button type="button" className="sg-refresh" onClick={loadStats} disabled={statsLoading}>
          {statsLoading ? "…" : "Refresh"}
        </button>
      </header>

      {stats ? (
        <>
          <div className="sg-stat-grid">
            <div className="sg-stat">
              <strong>{stats.todayScored}</strong>
              <span>Scored</span>
            </div>
            <div className="sg-stat">
              <strong>{stats.todayBlocked}</strong>
              <span>Blocked</span>
            </div>
            <div className="sg-stat">
              <strong>{stats.todayOverrides}</strong>
              <span>Overrides</span>
            </div>
            <div className="sg-stat">
              <strong>{stats.avgBuyer}</strong>
              <span>Avg buyer</span>
            </div>
            <div className="sg-stat">
              <strong>{stats.avgDeliv}</strong>
              <span>Avg risk</span>
            </div>
          </div>

          {stats.topReasons.length > 0 && (
            <div className="sg-stat-block">
              <p className="sg-field-label">Top issues</p>
              <div className="sg-tags">
                {stats.topReasons.map(([tag, count]) => (
                  <span key={tag} className="sg-badge">
                    {tag} ({count})
                  </span>
                ))}
              </div>
            </div>
          )}

          {stats.recentScores.length > 0 && (
            <div className="sg-stat-block">
              <p className="sg-field-label">Recent scores</p>
              <ul className="sg-score-list">
                {stats.recentScores.map((s, i) => (
                  <li key={i}>
                    <span className="sg-score-num">{s.score}/10</span>
                    <span>B:{s.buyer}</span>
                    <span>R:{s.deliv}</span>
                    {s.action === "block" && <span className="sg-score-flag">Blocked</span>}
                    {s.override && <span className="sg-score-flag">Override</span>}
                    <span className="sg-score-time">
                      {s.date
                        ? new Date(s.date).toLocaleTimeString("de-DE", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="sg-field-hint sg-stats-foot">{stats.total} scores in log</p>
        </>
      ) : (
        <p className="sg-field-hint">
          {statsLoading ? "Loading…" : "No scoring data yet. Stats appear after emails are scored."}
        </p>
      )}
    </div>
  );
};

export default PreSendScorerSettings;
